// The price-range band: p10 / median / average / p90 of real per-hospital
// complete bills on one green→gold→red scale, with an optional pin for one
// named hospital. Shared by the CPT/HCPCS code pages, the scenario detail
// card and the Atlas scenario block. The `.prb` stylesheet carries the few
// layout utilities the markup uses so the band renders the same where
// Tailwind is not loaded (the Atlas PWA).
import { useLayoutEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import "./PriceRangeBand.css";

/** Compact price-range band: p10 / median / avg / p90 markers on one scale.
 * Mirrors the video chart language: green cheapest, gold median, ink average,
 * red priciest. Hidden entirely when percentile data is missing. */
/** A specific hospital's listed price to place on the band (the rankings
 *  drilldown passes the hospital it is showing via ?hid&hp&hn). */
export interface BandHospital { name: string; price: number }

/** Marker and pin wording, for surfaces that render in another language (the
 *  city embed's Spanish). Omitted → the site's English labels, unchanged. */
export interface BandLabels { p10: string; median: string; avg: string; p90: string; pin: string }
const EN_LABELS: BandLabels = { p10: 'CHEAPEST 10%', median: 'MEDIAN', avg: 'AVERAGE', p90: 'PRICIEST 10%', pin: 'THIS HOSPITAL' };

export function PriceRangeBand({ p10, median, avg, p90, dark, compact, hospital, lesson = true, subject = "for this code", note, labels, pinCaption = true, fmt: fmtProp, aa = false, localRange }: {
  p10?: number; median?: number; avg?: number; p90?: number; dark?: boolean; compact?: boolean;
  hospital?: BandHospital | null;
  /** Translated marker labels; English when omitted. */
  labels?: BandLabels;
  /** The English sentence under a pinned hospital. Off where the caller writes its own. */
  pinCaption?: boolean;
  /** Money formatter (the embed formats per language). */
  fmt?: (v: number) => string;
  /** AA contrast for the small labels (the city embed promises WCAG 2.1 AA):
   *  deeper shades on light grounds, lighter on dark, printed solid. The
   *  site's own look is unchanged when omitted. */
  aa?: boolean;
  /** Shade the stretch of the national scale the reader's nearby hospitals
   *  span (scenario page "near you", 2026-09-29). */
  localRange?: { min: number; max: number } | null;
  /** Show the "?" median-vs-average lesson buttons (needs the site's dialog styles). */
  lesson?: boolean;
  /** What the named hospital's price is for ("for this code", "for this scenario"). */
  subject?: string;
  /** Provenance sentence after the hospital's placement; the code-page default when omitted. */
  note?: string;
}) {
  const [lessonOpen, setLessonOpen] = useState(false);
  // Marker labels sit centred under their tick; when a centred label would run
  // off the band (the ends, on a narrow card) it is nudged inward by exactly the
  // overflow — measured, so a wide card keeps every label dead-centre (Trent,
  // 2026-09-27: "PRICIEST 10% is not under the red marker").
  const boxRef = useRef<HTMLDivElement>(null);
  const [nudge, setNudge] = useState<Record<string, number>>({});
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const W = box.clientWidth;
      const next: Record<string, number> = {};
      box.querySelectorAll<HTMLElement>('[data-prb-label]').forEach((el) => {
        const w = el.offsetWidth, c = (W * Number(el.dataset.pp)) / 100;
        const left = c - w / 2;
        const d = left < 0 ? -left : left + w > W ? W - (left + w) : 0;
        if (d) next[el.dataset.prbLabel!] = Math.round(d);
      });
      setNudge((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [p10, median, avg, p90, labels]);
  if (!p10 || !p90 || !median || p90 <= p10) return null;
  const lo = p10 * 0.9, hi = p90 * 1.08;
  const pos = (v: number) => Math.min(97, Math.max(3, ((v - lo) / (hi - lo)) * 100));
  const fmt = fmtProp ?? ((v: number) => '$' + Math.round(v).toLocaleString());
  const L = labels ?? EN_LABELS;
  const ink = dark ? '#F0EBE3' : '#1b1b1b';
  // Band colours. The gradient keeps the site hues; only the text shades move for AA.
  const C = aa
    ? (dark ? { p10: '#86e0ae', median: '#f2c75c', p90: '#ff9a9a' } : { p10: '#1d6b44', median: '#7a5800', p90: '#9e1b2b' })
    : { p10: '#2e8b57', median: '#b8860b', p90: '#B22234' };
  const labelOpacity = aa ? '' : ' opacity-70';
  // Where the named hospital's listed price sits. Off-scale prices pin to the
  // band's edge with an arrow so a 300× outlier still reads as "way past the
  // priciest 10%" instead of vanishing. Wording stays factual: "lists", the
  // hospital's own published number vs. the national distribution.
  const hosp = hospital && hospital.price > 0 ? hospital : null;
  const hospNavy = '#002868';
  const hospOff: 'lo' | 'hi' | null = hosp ? (hosp.price > hi ? 'hi' : hosp.price < lo ? 'lo' : null) : null;
  const hospBand = hosp
    ? hosp.price < p10 ? 'below the cheapest 10%'
      : hosp.price <= median ? 'between the cheapest 10% and the median'
      : hosp.price <= p90 ? 'between the median and the priciest 10%'
      : 'above the priciest 10%'
    : '';
  const hospMult = hosp ? hosp.price / median : 0;
  const hospMultText = hosp
    ? hospMult >= 10 ? `${Math.round(hospMult).toLocaleString()}× the national median`
      : hospMult >= 1.05 ? `${hospMult.toFixed(1)}× the national median`
      : hospMult >= 0.95 ? 'about the national median'
      : `${Math.round((1 - hospMult) * 100)}% below the national median`
    : '';
  // A marker label near either end anchors to that edge instead of centring on
  // its tick, so "CHEAPEST 10%" never half-overflows a narrow card (the pin
  // label below already works this way).
  const markers: Array<{ v: number; label: string; kind: 'p10' | 'median' | 'avg' | 'p90'; color: string; lane: 0 | 1 }> = [
    { v: p10, label: L.p10, kind: 'p10', color: C.p10, lane: 0 },
    { v: median, label: L.median, kind: 'median', color: C.median, lane: 1 },
    ...(avg && avg > 0 ? [{ v: avg, label: L.avg, kind: 'avg' as const, color: ink, lane: 0 as const }] : []),
    { v: p90, label: L.p90, kind: 'p90', color: C.p90, lane: 1 },
  ];
  return (
    <div className={compact ? 'prb' : 'prb mt-4 pt-4 border-t'} style={compact ? undefined : { borderColor: dark ? 'rgba(197,165,90,0.25)' : 'rgba(27,27,27,0.12)' }}>
      <div ref={boxRef} className="relative" style={{ height: hosp ? 112 : 84 }}>
        {/* top labels (lane 0) */}
        {markers.filter(m => m.lane === 0).map(m => (
          <div key={m.label} data-prb-label={m.kind} data-pp={pos(m.v)} className="absolute text-center" style={{ left: `${pos(m.v)}%`, transform: `translateX(calc(-50% + ${nudge[m.kind] ?? 0}px))`, top: 0, whiteSpace: 'nowrap' }}>
            <div className={`text-[9px] sm:text-[10px] font-bold tracking-wide${labelOpacity}`} style={{ color: m.color }}>
              {m.label}
              {lesson && (m.kind === 'avg' || m.kind === 'median') && (
                <button onClick={() => setLessonOpen(true)} aria-label="What is the difference between median and average?"
                  className="ml-1 inline-flex items-center justify-center rounded-full border align-middle"
                  style={{ width: 13, height: 13, fontSize: 9, lineHeight: 1, borderColor: m.color, color: m.color, opacity: 0.9 }}>?</button>
              )}
            </div>
            <div className="text-xs sm:text-sm font-bold" style={{ color: m.color }}>{fmt(m.v)}</div>
          </div>
        ))}
        {/* the band */}
        <div className="absolute left-0 right-0 rounded-full" style={{ top: 38, height: 8, background: 'linear-gradient(90deg, rgba(46,139,87,0.75), rgba(184,134,11,0.55), rgba(178,34,52,0.75))' }} />
        {localRange && localRange.max >= localRange.min && (
          <div
            aria-hidden="true"
            className="absolute rounded-full"
            style={{
              left: `${pos(localRange.min)}%`,
              width: `${Math.max(1.2, pos(localRange.max) - pos(localRange.min))}%`,
              top: 34, height: 16,
              background: dark ? 'rgba(125,166,212,0.30)' : 'rgba(0,40,104,0.16)',
              border: `1.5px solid ${dark ? 'rgba(125,166,212,0.85)' : 'rgba(0,40,104,0.55)'}`,
            }}
          />
        )}
        {markers.map(m => (
          <div key={m.label + '-tick'} className="absolute rounded-full" style={{ left: `${pos(m.v)}%`, transform: 'translateX(-50%)', top: 33, width: 4, height: 18, background: m.color }} />
        ))}
        {/* the named hospital: a taller navy pin through the band + a lane of its own */}
        {hosp && (
          <>
            <div
              className="absolute rounded-full"
              title={`${hosp.name} lists ${fmt(hosp.price)} ${subject} — ${hospMultText}, ${hospBand}`}
              style={{ left: `${pos(hosp.price)}%`, transform: 'translateX(-50%)', top: 26, width: 6, height: 32, background: dark ? '#8cc2ff' : hospNavy, boxShadow: `0 0 0 2px ${dark ? '#0b1a33' : '#fff'}` }}
            />
            {/* Label as a navy pill (white text) so it reads on the light AND dark
                card — the band's `dark` flag doesn't track the page theme. Near
                either edge it anchors to that edge instead of centering, so an
                off-scale price never half-overflows the card. */}
            {(() => {
              const pp = pos(hosp.price);
              const edge = pp >= 88 ? 'right' : pp <= 12 ? 'left' : null;
              const place = edge === 'right' ? { right: 0, textAlign: 'right' as const }
                : edge === 'left' ? { left: 0, textAlign: 'left' as const }
                : { left: `${pp}%`, transform: 'translateX(-50%)', textAlign: 'center' as const };
              return (
                <div className="absolute" style={{ top: 84, whiteSpace: 'nowrap', ...place }}>
                  <span
                    className="inline-block rounded px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold tracking-wide"
                    style={{ background: hospNavy, color: '#fff', boxShadow: '0 0 0 1px rgba(255,255,255,0.55)' }}
                  >
                    {hospOff === 'lo' ? '◀ ' : ''}▲ {L.pin} {fmt(hosp.price)}{hospOff === 'hi' ? ' ▶' : ''}
                  </span>
                </div>
              );
            })()}
          </>
        )}
        {/* bottom labels (lane 1) */}
        {markers.filter(m => m.lane === 1).map(m => (
          <div key={m.label} data-prb-label={m.kind} data-pp={pos(m.v)} className="absolute text-center" style={{ left: `${pos(m.v)}%`, transform: `translateX(calc(-50% + ${nudge[m.kind] ?? 0}px))`, top: 56, whiteSpace: 'nowrap' }}>
            <div className="text-xs sm:text-sm font-bold" style={{ color: m.color }}>{fmt(m.v)}</div>
            <div className={`text-[9px] sm:text-[10px] font-bold tracking-wide${labelOpacity}`} style={{ color: m.color }}>
              {m.label}
              {lesson && (m.kind === 'avg' || m.kind === 'median') && (
                <button onClick={() => setLessonOpen(true)} aria-label="What is the difference between median and average?"
                  className="ml-1 inline-flex items-center justify-center rounded-full border align-middle"
                  style={{ width: 13, height: 13, fontSize: 9, lineHeight: 1, borderColor: m.color, color: m.color, opacity: 0.9 }}>?</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {hosp && pinCaption && (
        <p className="mt-2 text-[11px] leading-snug" style={{ color: 'inherit', opacity: 0.85 }}>
          <strong>{hosp.name}</strong> lists <strong>{fmt(hosp.price)}</strong> {subject} —{' '}
          {hospMultText}, {hospBand}. {note ?? "From the hospital's own published chargemaster; the national figures are the same-code distribution across all reporting hospitals."}
        </p>
      )}
      <Dialog open={lessonOpen} onOpenChange={setLessonOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Median vs. Average — Dignity explains</DialogTitle>
          </DialogHeader>
          {lessonOpen && <MedianLessonPlayer />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Median-lesson clip + synced visual-novel dialogue box. Caption windows are
 * proportional to spoken-character counts over the known narration length,
 * mirroring how the episode renderer times its captions. */
const LESSON_CUES: Array<{ t: number; text: string }> = [
  { t: 0.0, text: "Quick teacher's note, because you will see two numbers on every page — the average and the median — and they can sit far apart." },
  { t: 9.1, text: "Imagine one hundred hospitals. Ninety of them charge one hundred dollars for a procedure. Ten of them charge one hundred thousand." },
  { t: 17.9, text: "The average comes out over ten thousand dollars — but ninety times out of a hundred, you would pay one hundred." },
  { t: 25.4, text: "That is why the middle bill — the median — is the honest number, and why averages in American health care always look scarier than what most people actually pay." },
];

function MedianLessonPlayer() {
  const [cue, setCue] = useState(0);
  return (
    <div>
      <video
        controls
        autoPlay
        playsInline
        className="w-full"
        style={{ aspectRatio: '912/512', background: '#606663', display: 'block' }}
        src="https://static.americanhealthprices.com/video/AHP_median_lesson.mp4"
        onTimeUpdate={(e) => {
          const t = (e.target as HTMLVideoElement).currentTime;
          let i = 0;
          for (let k = 0; k < LESSON_CUES.length; k++) if (t >= LESSON_CUES[k].t) i = k;
          setCue(i);
        }}
      />
      {/* Visual-novel dialogue band, aligned to the video above */}
      <div style={{ background: '#1f3a5f', borderTop: '3px solid #B22234', padding: '10px 14px 14px' }}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 rounded-md overflow-hidden" style={{ width: 56, height: 56, border: '2px solid #C5A55A', background: '#f5efe0' }}>
            <img src="/dignity-reference.png" alt="Dignity" className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <div className="flex-1 rounded-md px-4 py-3" style={{ background: '#f7f3e8', minHeight: 76 }}>
            <p className="text-[10px] font-bold tracking-widest mb-1" style={{ color: '#B22234' }}>DIGNITY</p>
            <p className="text-[15px] leading-snug" style={{ color: '#3B2F23', fontFamily: "Georgia, 'Times New Roman', serif" }}>
              {LESSON_CUES[cue].text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
